import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { type Dictionary, useText } from '../i18n/LocaleProvider';
import { SESSION_KEY, safeNext, tokenFromFragment } from './session';

// Phase 1 authentication pages (V1-1-10; HAA C-1, C-2, T-1 to T-3). Every
// failure shows ONE generic message (the server answers 401
// invalid_credentials identically for unknown accounts, wrong passwords,
// locked accounts and wrong codes). Challenges and secrets live only in page
// state; nothing is stored in the browser.

const text: Dictionary<
  | 'signIn'
  | 'email'
  | 'password'
  | 'submit'
  | 'working'
  | 'invalid'
  | 'tooMany'
  | 'unavailable'
  | 'forgot'
  | 'mfaTitle'
  | 'mfaHint'
  | 'code'
  | 'verify'
  | 'useRecovery'
  | 'useAuthenticator'
  | 'recoveryCode'
  | 'recoveryHint'
  | 'enrollTitle'
  | 'enrollHint'
  | 'enrollSecret'
  | 'enrollUri'
  | 'enrollConfirm'
  | 'codesTitle'
  | 'codesHint'
  | 'codesAcknowledge'
  | 'continue'
  | 'expiredChallenge'
  | 'acceptTitle'
  | 'acceptHint'
  | 'newPassword'
  | 'confirmPassword'
  | 'passwordRule'
  | 'mismatch'
  | 'tooShort'
  | 'compromised'
  | 'setPassword'
  | 'passwordSet'
  | 'goToSignIn'
  | 'invalidLink'
  | 'forgotTitle'
  | 'forgotHint'
  | 'sendLink'
  | 'forgotSent'
  | 'resetTitle'
  | 'resetDone'
> = {
  en: {
    signIn: 'Sign in',
    email: 'Email',
    password: 'Password',
    submit: 'Continue',
    working: 'Please wait…',
    invalid: 'The sign-in details are not valid, or the account is temporarily locked.',
    tooMany: 'Too many attempts. Please wait a minute and try again.',
    unavailable: 'The service is temporarily unavailable. Please try again.',
    forgot: 'Forgot your password?',
    mfaTitle: 'Two-factor authentication',
    mfaHint: 'Enter the 6-digit code from your authenticator app.',
    code: 'Code',
    verify: 'Verify',
    useRecovery: 'Use a recovery code instead',
    useAuthenticator: 'Use the authenticator app instead',
    recoveryCode: 'Recovery code',
    recoveryHint: 'Each recovery code works once.',
    enrollTitle: 'Set up two-factor authentication',
    enrollHint:
      'Two-factor authentication is required. Add this account to an authenticator app, then enter the 6-digit code it shows.',
    enrollSecret: 'Setup key (enter it manually in the app):',
    enrollUri: 'Or open this setup link on the device with the app:',
    enrollConfirm: 'Confirm and continue',
    codesTitle: 'Save your recovery codes',
    codesHint:
      'These ten codes are shown only now. Each one lets you sign in once if you lose your authenticator. Store them securely.',
    codesAcknowledge: 'I have stored these recovery codes securely.',
    continue: 'Continue',
    expiredChallenge: 'This sign-in step has expired. Please sign in again.',
    acceptTitle: 'Set up your account',
    acceptHint: 'Choose your password. You will then sign in and set up two-factor authentication.',
    newPassword: 'New password',
    confirmPassword: 'Repeat the password',
    passwordRule: 'At least 15 characters. A passphrase of several words works well.',
    mismatch: 'The two passwords do not match.',
    tooShort: 'The password must have at least 15 characters.',
    compromised: 'This password is known to be unsafe. Choose a different one.',
    setPassword: 'Set password',
    passwordSet: 'Your password is set.',
    goToSignIn: 'Go to sign in',
    invalidLink: 'This link is invalid, has expired or was already used.',
    forgotTitle: 'Reset your password',
    forgotHint: 'Enter your email. If an account exists, we will send a reset link valid for 30 minutes.',
    sendLink: 'Send reset link',
    forgotSent: 'If an account exists for this address, a reset link has been sent.',
    resetTitle: 'Choose a new password',
    resetDone: 'Your password has been changed. All earlier sessions were signed out.',
  },
  ar: {
    signIn: 'تسجيل الدخول',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    submit: 'متابعة',
    working: 'يرجى الانتظار…',
    invalid: 'بيانات الدخول غير صحيحة، أو أن الحساب مقفل مؤقتًا.',
    tooMany: 'محاولات كثيرة. يرجى الانتظار دقيقة ثم المحاولة مرة أخرى.',
    unavailable: 'الخدمة غير متاحة مؤقتًا. يرجى المحاولة مرة أخرى.',
    forgot: 'نسيت كلمة المرور؟',
    mfaTitle: 'التحقق بخطوتين',
    mfaHint: 'أدخل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.',
    code: 'الرمز',
    verify: 'تحقق',
    useRecovery: 'استخدام رمز استرداد بدلًا من ذلك',
    useAuthenticator: 'استخدام تطبيق المصادقة بدلًا من ذلك',
    recoveryCode: 'رمز الاسترداد',
    recoveryHint: 'يعمل كل رمز استرداد مرة واحدة فقط.',
    enrollTitle: 'إعداد التحقق بخطوتين',
    enrollHint: 'التحقق بخطوتين إلزامي. أضف هذا الحساب إلى تطبيق مصادقة، ثم أدخل الرمز المكوّن من 6 أرقام الظاهر فيه.',
    enrollSecret: 'مفتاح الإعداد (أدخله يدويًا في التطبيق):',
    enrollUri: 'أو افتح رابط الإعداد هذا على الجهاز الذي يحتوي على التطبيق:',
    enrollConfirm: 'تأكيد ومتابعة',
    codesTitle: 'احفظ رموز الاسترداد',
    codesHint:
      'تظهر هذه الرموز العشرة الآن فقط. يتيح لك كل رمز تسجيل الدخول مرة واحدة إذا فقدت تطبيق المصادقة. احفظها في مكان آمن.',
    codesAcknowledge: 'حفظت رموز الاسترداد في مكان آمن.',
    continue: 'متابعة',
    expiredChallenge: 'انتهت صلاحية خطوة تسجيل الدخول هذه. يرجى تسجيل الدخول مرة أخرى.',
    acceptTitle: 'إعداد حسابك',
    acceptHint: 'اختر كلمة المرور، ثم سجّل الدخول وأعدّ التحقق بخطوتين.',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'أعد كتابة كلمة المرور',
    passwordRule: '15 حرفًا على الأقل. عبارة من عدة كلمات خيار جيد.',
    mismatch: 'كلمتا المرور غير متطابقتين.',
    tooShort: 'يجب أن تتكوّن كلمة المرور من 15 حرفًا على الأقل.',
    compromised: 'كلمة المرور هذه معروفة بأنها غير آمنة. اختر كلمة أخرى.',
    setPassword: 'تعيين كلمة المرور',
    passwordSet: 'تم تعيين كلمة المرور.',
    goToSignIn: 'الانتقال إلى تسجيل الدخول',
    invalidLink: 'هذا الرابط غير صالح أو منتهي الصلاحية أو سبق استخدامه.',
    forgotTitle: 'إعادة تعيين كلمة المرور',
    forgotHint: 'أدخل بريدك الإلكتروني. إذا كان الحساب موجودًا فسنرسل رابطًا صالحًا لمدة 30 دقيقة.',
    sendLink: 'إرسال رابط إعادة التعيين',
    forgotSent: 'إذا كان هناك حساب لهذا العنوان فقد أُرسل رابط إعادة التعيين.',
    resetTitle: 'اختر كلمة مرور جديدة',
    resetDone: 'تم تغيير كلمة المرور، وتم تسجيل الخروج من جميع الجلسات السابقة.',
  },
};

type Text = ReturnType<typeof useText<keyof (typeof text)['en']>>;

function failureMessage(t: Text, status: number): string {
  if (status === 429) return t.tooMany;
  if (status >= 500) return t.unavailable;
  return t.invalid;
}

function AuthCard({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <main className="er-auth">
      <h1>{title}</h1>
      {children}
    </main>
  );
}

function Alert({ kind, children }: { readonly kind: 'error' | 'success' | 'info'; readonly children: ReactNode }) {
  return (
    <p className={`er-alert ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </p>
  );
}

// The challenge flows through router state only (never the URL or storage).
interface ChallengeState {
  readonly challenge: string;
  readonly email: string;
  readonly next: string;
}

function useAfterSession(next: string) {
  const queries = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    await queries.invalidateQueries({ queryKey: SESSION_KEY });
    navigate(next, { replace: true });
  };
}

export function LoginPage() {
  const client = useApiClient();
  const t = useText(text);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const done = useAfterSession(next);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await client.login({ body: { email, password } });
      if (response.status !== 200) {
        setError(failureMessage(t, response.status));
        return;
      }
      setPassword('');
      const state: ChallengeState = { challenge: response.body.challenge ?? '', email, next };
      if (response.body.status === 'mfa_required') navigate('/login/mfa', { state });
      else if (response.body.status === 'mfa_enrollment_required') navigate('/login/mfa-setup', { state });
      else await done();
    } catch {
      setError(t.unavailable);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard title={t.signIn}>
      {error && <Alert kind="error">{error}</Alert>}
      <form onSubmit={submit} noValidate>
        <div className="er-field">
          <label htmlFor="login-email">{t.email}</label>
          <input
            id="login-email"
            type="email"
            autoComplete="username"
            dir="ltr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="er-field">
          <label htmlFor="login-password">{t.password}</label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="er-actions">
          <button className="er-button" type="submit" disabled={busy || !email || !password}>
            {busy ? t.working : t.submit}
          </button>
          <Link to="/forgot-password">{t.forgot}</Link>
        </div>
      </form>
    </AuthCard>
  );
}

function useChallenge(): ChallengeState | null {
  const state = useLocation().state as ChallengeState | null;
  return state?.challenge ? state : null;
}

function ExpiredChallenge({ t }: { readonly t: Text }) {
  return (
    <AuthCard title={t.signIn}>
      <Alert kind="info">{t.expiredChallenge}</Alert>
      <Link to="/login">{t.goToSignIn}</Link>
    </AuthCard>
  );
}

export function MfaPage() {
  const client = useApiClient();
  const t = useText(text);
  const challenge = useChallenge();
  const done = useAfterSession(challenge?.next ?? '/');
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!challenge) return <ExpiredChallenge t={t} />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const body = { challenge: challenge.challenge, code: code.trim() };
      const response = recovery ? await client.loginWithRecoveryCode({ body }) : await client.verifyMfa({ body });
      if (response.status === 200) await done();
      else setError(failureMessage(t, response.status));
    } catch {
      setError(t.unavailable);
    } finally {
      setBusy(false);
      setCode('');
    }
  }

  return (
    <AuthCard title={t.mfaTitle}>
      <p className="er-hint">{recovery ? t.recoveryHint : t.mfaHint}</p>
      {error && <Alert kind="error">{error}</Alert>}
      <form onSubmit={submit} noValidate>
        <div className="er-field">
          <label htmlFor="mfa-code">{recovery ? t.recoveryCode : t.code}</label>
          <input
            id="mfa-code"
            dir="ltr"
            autoComplete="one-time-code"
            inputMode={recovery ? 'text' : 'numeric'}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </div>
        <div className="er-actions">
          <button className="er-button" type="submit" disabled={busy || !code}>
            {busy ? t.working : t.verify}
          </button>
          <button className="er-link-button" type="button" onClick={() => setRecovery(!recovery)}>
            {recovery ? t.useAuthenticator : t.useRecovery}
          </button>
        </div>
      </form>
    </AuthCard>
  );
}

export function MfaSetupPage() {
  const client = useApiClient();
  const t = useText(text);
  const challenge = useChallenge();
  const done = useAfterSession(challenge?.next ?? '/');
  const [enrollment, setEnrollment] = useState<{ secret: string; provisioningUri: string } | null>(null);
  const [codes, setCodes] = useState<readonly string[] | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!challenge || enrollment) return;
    let cancelled = false;
    client
      .beginMfaEnrollment({ body: { challenge: challenge.challenge, accountLabel: challenge.email } })
      .then((response) => {
        if (cancelled) return;
        if (response.status === 200) setEnrollment(response.body);
        else setError(failureMessage(t, response.status));
      })
      .catch(() => !cancelled && setError(t.unavailable));
    return () => {
      cancelled = true;
    };
  }, [challenge, enrollment, client, t]);

  if (!challenge) return <ExpiredChallenge t={t} />;

  async function confirm(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const response = await client.confirmMfaEnrollment({
        body: { challenge: challenge.challenge, code: code.trim() },
      });
      if (response.status === 200) setCodes(response.body.recoveryCodes);
      else setError(response.status === 400 ? t.invalid : failureMessage(t, response.status));
    } catch {
      setError(t.unavailable);
    } finally {
      setBusy(false);
      setCode('');
    }
  }

  if (codes) {
    return (
      <AuthCard title={t.codesTitle}>
        <p className="er-hint">{t.codesHint}</p>
        <ul className="er-codes" aria-label={t.codesTitle} data-testid="recovery-codes">
          {codes.map((c) => (
            <li key={c} style={{ listStyle: 'none' }}>
              {c}
            </li>
          ))}
        </ul>
        <div className="er-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBlockStart: 16 }}>
          <input
            id="codes-ack"
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <label htmlFor="codes-ack">{t.codesAcknowledge}</label>
        </div>
        <button className="er-button" type="button" disabled={!acknowledged} onClick={() => void done()}>
          {t.continue}
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t.enrollTitle}>
      <p className="er-hint">{t.enrollHint}</p>
      {error && <Alert kind="error">{error}</Alert>}
      {enrollment && (
        <>
          <p>{t.enrollSecret}</p>
          <p className="er-mono" data-testid="totp-secret">
            {enrollment.secret.replace(/(.{4})(?=.)/g, '$1 ')}
          </p>
          <p>
            {t.enrollUri}{' '}
            <a className="er-mono" href={enrollment.provisioningUri}>
              otpauth://
            </a>
          </p>
          <form onSubmit={confirm} noValidate>
            <div className="er-field">
              <label htmlFor="enroll-code">{t.code}</label>
              <input
                id="enroll-code"
                dir="ltr"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <button className="er-button" type="submit" disabled={busy || code.trim().length !== 6}>
              {busy ? t.working : t.enrollConfirm}
            </button>
          </form>
        </>
      )}
    </AuthCard>
  );
}

// Shared by invitation acceptance and password reset: the token comes from
// the URL fragment and is removed from the address bar immediately.
function useFragmentToken(): string | null {
  const [token] = useState(() => tokenFromFragment(window.location.hash));
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  return token;
}

function NewPasswordForm({
  t,
  submitLabel,
  onSubmit,
}: {
  readonly t: Text;
  readonly submitLabel: string;
  readonly onSubmit: (password: string) => Promise<'done' | 'invalid_link' | 'too_short' | 'compromised' | 'failed'>;
}) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const length = [...password.normalize('NFC')].length;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== repeat) {
      setError(t.mismatch);
      return;
    }
    if (length < 15) {
      setError(t.tooShort);
      return;
    }
    setBusy(true);
    setError(null);
    const outcome = await onSubmit(password).catch(() => 'failed' as const);
    setBusy(false);
    if (outcome === 'done') return;
    setError(
      outcome === 'invalid_link'
        ? t.invalidLink
        : outcome === 'too_short'
          ? t.tooShort
          : outcome === 'compromised'
            ? t.compromised
            : t.unavailable,
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      {error && <Alert kind="error">{error}</Alert>}
      <div className="er-field">
        <label htmlFor="new-password">{t.newPassword}</label>
        <input
          id="new-password"
          type="password"
          autoComplete="new-password"
          aria-describedby="password-rule"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <span id="password-rule" className="er-hint">
          {t.passwordRule}
        </span>
      </div>
      <div className="er-field">
        <label htmlFor="repeat-password">{t.confirmPassword}</label>
        <input
          id="repeat-password"
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
        />
      </div>
      <button className="er-button" type="submit" disabled={busy || !password || !repeat}>
        {busy ? t.working : submitLabel}
      </button>
    </form>
  );
}

type PasswordResponse = { readonly status: number; readonly body: unknown };
function passwordOutcome(response: PasswordResponse): 'done' | 'invalid_link' | 'too_short' | 'compromised' | 'failed' {
  if (response.status === 200) return 'done';
  if (response.status === 401) return 'invalid_link';
  if (response.status === 400) {
    const issues = (response.body as { error?: { issues?: { code: string }[] } }).error?.issues ?? [];
    if (issues.some((i) => i.code === 'password_compromised')) return 'compromised';
    if (issues.some((i) => i.code === 'password_too_short')) return 'too_short';
  }
  return 'failed';
}

function TokenPasswordPage({
  title,
  hint,
  doneMessage,
  submit,
}: {
  readonly title: string;
  readonly hint: string;
  readonly doneMessage: string;
  readonly submit: (token: string, password: string) => Promise<PasswordResponse>;
}) {
  const t = useText(text);
  const token = useFragmentToken();
  const [done, setDone] = useState(false);
  if (!token) {
    return (
      <AuthCard title={title}>
        <Alert kind="error">{t.invalidLink}</Alert>
        <Link to="/login">{t.goToSignIn}</Link>
      </AuthCard>
    );
  }
  if (done) {
    return (
      <AuthCard title={title}>
        <Alert kind="success">{doneMessage}</Alert>
        <Link to="/login">{t.goToSignIn}</Link>
      </AuthCard>
    );
  }
  return (
    <AuthCard title={title}>
      <p className="er-hint">{hint}</p>
      <NewPasswordForm
        t={t}
        submitLabel={t.setPassword}
        onSubmit={async (password) => {
          const outcome = passwordOutcome(await submit(token, password));
          if (outcome === 'done') setDone(true);
          return outcome;
        }}
      />
    </AuthCard>
  );
}

export function AcceptInvitationPage() {
  const client = useApiClient();
  const t = useText(text);
  return (
    <TokenPasswordPage
      title={t.acceptTitle}
      hint={t.acceptHint}
      doneMessage={t.passwordSet}
      submit={(token, password) => client.acceptInvitation({ body: { token, password } })}
    />
  );
}

export function ResetPasswordPage() {
  const client = useApiClient();
  const t = useText(text);
  return (
    <TokenPasswordPage
      title={t.resetTitle}
      hint={t.passwordRule}
      doneMessage={t.resetDone}
      submit={(token, password) => client.completePasswordReset({ body: { token, password } })}
    />
  );
}

export function ForgotPasswordPage() {
  const client = useApiClient();
  const t = useText(text);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await client.requestPasswordReset({ body: { email } });
      if (response.status === 202) setSent(true);
      else setError(failureMessage(t, response.status));
    } catch {
      setError(t.unavailable);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard title={t.forgotTitle}>
      {sent ? (
        <Alert kind="success">{t.forgotSent}</Alert>
      ) : (
        <>
          <p className="er-hint">{t.forgotHint}</p>
          {error && <Alert kind="error">{error}</Alert>}
          <form onSubmit={submit} noValidate>
            <div className="er-field">
              <label htmlFor="forgot-email">{t.email}</label>
              <input
                id="forgot-email"
                type="email"
                dir="ltr"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button className="er-button" type="submit" disabled={busy || !email}>
              {busy ? t.working : t.sendLink}
            </button>
          </form>
        </>
      )}
      <p>
        <Link to="/login">{t.goToSignIn}</Link>
      </p>
    </AuthCard>
  );
}
